import {ChangeDetectionStrategy, Component, input, output} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors} from '@angular/forms';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatError, MatFormField, MatHint, MatLabel} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatOption, MatSelect} from '@angular/material/select';
import {TranslateModule} from '@ngx-translate/core';
import {FeltValidatorer} from 'felles-ng2/infrastruktur/angular/feltValidatorer';
import {HtmlTekst, KorrelertSpørsmålModell, RangeringValg, SpørsmålId, SpørsmålKorrelasjonModell, SpørsmålModell, SpørsmålOppsett, SpørsmålSpesifikasjon, Spørsmålstype, Uuid} from 'felles-ng2/tjenester/ressurser/generertNg2HttpKlient';
import {i18n} from 'felles-ng2/tjenester/i18n/Oversettelser';
import {InlineMeldingKomponent} from 'web-ng2/delt/komponenter/inline-melding/inline-melding';
import {RiktekstRedigeringKomponent} from 'web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering';
import {Korrelasjon, TypeOgOppsett} from './Korrelasjon';

export type SpørsmålSkjema = ReturnType<typeof SpørreskjemaSpørsmålRedigerKomponent.opprettSkjema>;
export type AlternativSkjema = ReturnType<typeof SpørreskjemaSpørsmålRedigerKomponent.opprettAlternativskjema>;

/** Verdi i nedtrekkslisten for valg av alternativkilde som angir at spørsmålet har egne alternativer */
export const egneAlternativer = 'egneAlternativer';

/** Farger som foreslås for nye alternativer, i rekkefølge */
const standardfarger = ['#1f7303', '#44cf13', '#009dff', '#fa6565', '#b30202', '#777777'];

/**
 * Redigering av et spørsmål i et spørreskjema, se {@link SpørreskjemaRedigerKomponent}.
 * For skjema som er i bruk (erLaast) kan kun tekster og farger endres.
 *
 * Spørsmål av typen rangering, velgEnAvFlere og velgNoenAvFlere kan ha egne alternativer, eller benytte samme alternativer som et annet spørsmål
 * (alternativkilde). Koblingen finnes kun på klienten, ved lagring sendes alternativene til kilden, se {@link somSpesifikasjon}.
 *
 * Spørsmål kan korrelere med spørsmål i andre skjemaer (feks i en kopi av skjemaet), slik at resultatene sammenliknes på tvers av
 * undersøkelser. Komponenten viser hvilke spørsmål spørsmålet korrelerer med, og varsler dersom endret type eller svaralternativer
 * gjør at korrelasjonen brytes ved lagring, se {@link Korrelasjon}.
 */
@Component({
    selector: 'spoerreskjema-spoersmaal-rediger',
    templateUrl: 'spørreskjema-spørsmål-rediger.html',
    styleUrl: 'spørreskjema-spørsmål-rediger.scss',
    host: {'data-testid': 'spoerreskjema-spoersmaal-rediger', '[class.gruppe]': 'erGruppe()'},
    imports: [
        ReactiveFormsModule,
        MatButton,
        MatIconButton,
        MatError,
        MatFormField,
        MatHint,
        MatIcon,
        MatInput,
        MatLabel,
        MatOption,
        MatSelect,
        TranslateModule,
        RiktekstRedigeringKomponent,
        InlineMeldingKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpørreskjemaSpørsmålRedigerKomponent {

    skjema = input.required<SpørsmålSkjema>();
    /** Sann dersom skjemaet er i bruk av en eller flere undersøkelser, kun tekster og farger kan da endres */
    erLaast = input(false);
    /** Spørsmål som kan velges som alternativkilde, se Alternativkilder.kandidaterFor() */
    alternativkandidater = input<SpørsmålSkjema[]>([]);
    /** Korrelerte spørsmål med annen type eller annet oppsett enn spørsmålet slik det nå er redigert, se {@link avvikendeKorrelerte} */
    avvikendeKorrelerte = input<KorrelertSpørsmålModell[]>([]);
    slett = output<void>();

    readonly egneAlternativer = egneAlternativer;

    readonly spoersmaalstyper = [
        {verdi: Spørsmålstype.grupperingstittel, navn: i18n('Gruppe')},
        {verdi: Spørsmålstype.fritekst, navn: i18n('Fritekst')},
        {verdi: Spørsmålstype.boolsk, navn: i18n('Ja/nei')},
        {verdi: Spørsmålstype.rangering, navn: i18n('Rangering')},
        {verdi: Spørsmålstype.velgEnAvFlere, navn: i18n('Velg en av flere')},
        {verdi: Spørsmålstype.velgNoenAvFlere, navn: i18n('Velg noen av flere')}
    ];

    /** @param korrelasjon spørsmålets korellasjonsId og korrelerte spørsmål i andre skjemaer, null for nytt spørsmål */
    static opprettSkjema(spørsmål: Partial<SpørsmålModell> = {}, erLåst = false, korrelasjon: SpørsmålKorrelasjonModell | null = null) {
        const alternativer = spørsmål.oppsett?.rangering?.alternativer ?? spørsmål.oppsett?.velgEnAvFlere?.alternativer ?? spørsmål.oppsett?.velgNoenAvFlere?.alternativer ?? [];
        const skjema = new FormGroup({
            id: new FormControl<SpørsmålId | null>(spørsmål.id ?? null),
            type: new FormControl<Spørsmålstype>(spørsmål.type ?? Spørsmålstype.fritekst, {nonNullable: true}),
            tittel: new FormControl<string>(spørsmål.tittel ?? '', {nonNullable: true, validators: FeltValidatorer.litenIkkeTomStreng}),
            beskrivelse: new FormControl<HtmlTekst | null>(spørsmål.beskrivelse ?? ''),
            alternativer: new FormArray(alternativer.map(alternativ => SpørreskjemaSpørsmålRedigerKomponent.opprettAlternativskjema(alternativ, erLåst))),
            //spørsmålet alternativene hentes fra, null dersom spørsmålet har egne alternativer. Typet som FormGroup da SpørsmålSkjema ikke kan referere til seg selv
            alternativkilde: new FormControl<FormGroup | null>(null),
            //maksimalt antall alternativer respondenten kan velge, null dersom det ikke er noen begrensning. Kun aktuell for velgNoenAvFlere.
            //inngår i oppsettet som må være uendret for skjema som er i bruk
            //valideres av maksimaltAntallValgValidator, da gyldig verdi avhenger av type og antall alternativer
            maksimaltAntallValg: new FormControl<number | null>({value: spørsmål.oppsett?.velgNoenAvFlere?.maksimaltAntallValg ?? null, disabled: erLåst}),
            //korellasjonsId og korrelerte spørsmål i andre skjemaer slik de er lagret på serveren, endres ikke ved redigering.
            //korellasjonsId sendes ved lagring med mindre korrelasjonen må brytes, se somSpesifikasjon()
            korellasjonsId: new FormControl<Uuid | null>(korrelasjon?.korellasjonsId ?? null),
            korrelerteSpoersmaal: new FormControl<KorrelertSpørsmålModell[]>(korrelasjon?.korrelerteSpoersmaal ?? [], {nonNullable: true})
        }, {validators: [SpørreskjemaSpørsmålRedigerKomponent.alternativerPåkrevetValidator, SpørreskjemaSpørsmålRedigerKomponent.maksimaltAntallValgValidator]});
        if (erLåst) skjema.controls.type.disable();
        return skjema;
    }

    static opprettAlternativskjema(alternativ: RangeringValg, erLåst = false) {
        return new FormGroup({
            //tittel inngår i oppsettet som må være uendret for skjema som er i bruk
            tittel: new FormControl<string>({value: alternativ.tittel, disabled: erLåst}, {nonNullable: true, validators: FeltValidatorer.litenIkkeTomStreng}),
            verdi: new FormControl<number>(alternativ.verdi, {nonNullable: true}),
            farge: new FormControl<string>(alternativ.farge, {nonNullable: true})
        });
    }

    /** Sann dersom spørsmålstypen lar respondenten velge flere alternativer, og dermed kan ha maksimalt antall valg */
    static harMaksimaltAntallValg(type: Spørsmålstype): boolean {
        return type === Spørsmålstype.velgNoenAvFlere;
    }

    static harAlternativer(type: Spørsmålstype): boolean {
        return type === Spørsmålstype.rangering || type === Spørsmålstype.velgEnAvFlere || type === Spørsmålstype.velgNoenAvFlere;
    }

    /** Spørsmålet alternativene hentes fra, null dersom spørsmålet har egne alternativer */
    static kildeFor(skjema: SpørsmålSkjema): SpørsmålSkjema | null {
        return skjema.controls.alternativkilde.value as SpørsmålSkjema | null;
    }

    /** Alternativene som gjelder for spørsmålet, dvs alternativene til kilden dersom spørsmålet har en alternativkilde */
    static alternativerFor(skjema: SpørsmålSkjema): RangeringValg[] {
        return (SpørreskjemaSpørsmålRedigerKomponent.kildeFor(skjema) ?? skjema).controls.alternativer.getRawValue();
    }

    /** Erstatter spørsmålets egne alternativer med en kopi av alternativene til angitt kilde, og fjerner koblingen til kilden */
    static kopierAlternativerFraKilde(skjema: SpørsmålSkjema, kilde: SpørsmålSkjema, erLåst = false): void {
        const alternativer = skjema.controls.alternativer;
        alternativer.clear({emitEvent: false});
        kilde.controls.alternativer.getRawValue()
          .forEach(alternativ => alternativer.push(SpørreskjemaSpørsmålRedigerKomponent.opprettAlternativskjema(alternativ, erLåst), {emitEvent: false}));
        skjema.controls.alternativkilde.setValue(null, {emitEvent: false});
        skjema.updateValueAndValidity({emitEvent: false});
    }

    /** Spesifikasjon for lagring av spørsmålet. Korellasjonsid utelates dersom korrelasjonen må brytes, se {@link korrelasjonBrytes} */
    static somSpesifikasjon(skjema: SpørsmålSkjema): SpørsmålSpesifikasjon {
        const verdier = skjema.getRawValue();
        const {type, oppsett} = SpørreskjemaSpørsmålRedigerKomponent.typeOgOppsett(skjema);
        return {
            id: verdier.id,
            tittel: verdier.tittel.trim(),
            beskrivelse: verdier.beskrivelse ?? '',
            type,
            oppsett,
            korellasjonsId: SpørreskjemaSpørsmålRedigerKomponent.korrelasjonBrytes(skjema) ? null : verdier.korellasjonsId
        };
    }

    /** Korrelerte spørsmål i andre skjemaer med annen type eller annet oppsett enn spørsmålet slik det nå er redigert */
    static avvikendeKorrelerte(skjema: SpørsmålSkjema): KorrelertSpørsmålModell[] {
        return Korrelasjon.avvikendeSpørsmål(SpørreskjemaSpørsmålRedigerKomponent.typeOgOppsett(skjema), skjema.controls.korrelerteSpoersmaal.value);
    }

    /** Sann dersom korrelasjonen med spørsmål i andre skjemaer brytes ved lagring, pga endret type eller svaralternativer */
    static korrelasjonBrytes(skjema: SpørsmålSkjema): boolean {
        return SpørreskjemaSpørsmålRedigerKomponent.avvikendeKorrelerte(skjema).length > 0;
    }

    /** Typen og oppsettet til spørsmålet slik det sendes til serveren, oppsettet er null for typer uten svaralternativer */
    static typeOgOppsett(skjema: SpørsmålSkjema): TypeOgOppsett & {oppsett: SpørsmålOppsett | null} {
        const verdier = skjema.getRawValue();
        const alternativer = SpørreskjemaSpørsmålRedigerKomponent.alternativerFor(skjema)
          .map(alternativ => ({tittel: alternativ.tittel.trim(), verdi: alternativ.verdi, farge: alternativ.farge}));
        return {
            type: verdier.type,
            oppsett: verdier.type === Spørsmålstype.rangering ? {rangering: {alternativer}}
              : verdier.type === Spørsmålstype.velgEnAvFlere ? {velgEnAvFlere: {alternativer}}
              : verdier.type === Spørsmålstype.velgNoenAvFlere ? {velgNoenAvFlere: {alternativer, maksimaltAntallValg: verdier.maksimaltAntallValg ?? undefined}}
              : null
        };
    }

    /** Spørsmål i andre skjemaer spørsmålet korrelerer med, slik det er lagret på serveren */
    korrelerteSpoersmaal(): KorrelertSpørsmålModell[] {
        return this.skjema().controls.korrelerteSpoersmaal.value;
    }

    erGruppe(): boolean {
        return this.skjema().controls.type.value === Spørsmålstype.grupperingstittel;
    }

    visMaksimaltAntallValg(): boolean {
        return SpørreskjemaSpørsmålRedigerKomponent.harMaksimaltAntallValg(this.skjema().controls.type.value);
    }

    visAlternativer(): boolean {
        return SpørreskjemaSpørsmålRedigerKomponent.harAlternativer(this.skjema().controls.type.value);
    }

    valgtAlternativkilde(): SpørsmålSkjema | null {
        return SpørreskjemaSpørsmålRedigerKomponent.kildeFor(this.skjema());
    }

    /** Verdi for nedtrekkslisten for valg av alternativkilde */
    valgtAlternativkildeVerdi(): SpørsmålSkjema | typeof egneAlternativer {
        return this.valgtAlternativkilde() ?? egneAlternativer;
    }

    /** Alternativene til valgt alternativkilde, tom dersom spørsmålet har egne alternativer */
    kildensAlternativer(): RangeringValg[] {
        const kilde = this.valgtAlternativkilde();
        return kilde ? kilde.controls.alternativer.getRawValue() : [];
    }

    kandidattittel(kandidat: SpørsmålSkjema): string {
        return kandidat.controls.tittel.value.trim() || '…';
    }

    /** Velger spørsmål alternativene skal hentes fra. Ved bytte til egne alternativer kopieres alternativene til tidligere kilde */
    velgAlternativkilde(valgt: SpørsmålSkjema | typeof egneAlternativer): void {
        const forrigeKilde = this.valgtAlternativkilde();
        if (valgt === egneAlternativer) {
            if (forrigeKilde) SpørreskjemaSpørsmålRedigerKomponent.kopierAlternativerFraKilde(this.skjema(), forrigeKilde);
            this.skjema().controls.alternativkilde.setValue(null);
        } else {
            this.skjema().controls.alternativkilde.setValue(valgt);
        }
    }

    /** Legger til alternativ med verdi én høyere enn høyeste eksisterende verdi, slik at eksisterende verdier ikke endres */
    leggTilAlternativ(): void {
        const alternativer = this.skjema().controls.alternativer;
        const verdier = alternativer.getRawValue().map(alternativ => alternativ.verdi);
        alternativer.push(SpørreskjemaSpørsmålRedigerKomponent.opprettAlternativskjema({
            tittel: '',
            verdi: verdier.length > 0 ? Math.max(...verdier) + 1 : 0,
            farge: standardfarger[alternativer.length % standardfarger.length]
        }));
    }

    fjernAlternativ(indeks: number): void {
        this.skjema().controls.alternativer.removeAt(indeks);
    }

    /** Validerer at maksimalt antall valg, dersom angitt, er et heltall mellom 1 og antall alternativer.
     * Kun aktuell for spørsmål av typen velgNoenAvFlere, for øvrige typer ignoreres verdien, se {@link somSpesifikasjon} */
    private static maksimaltAntallValgValidator(kontroll: AbstractControl): ValidationErrors | null {
        const skjema = kontroll as SpørsmålSkjema;
        const maks = skjema.controls.maksimaltAntallValg.value;
        if (!SpørreskjemaSpørsmålRedigerKomponent.harMaksimaltAntallValg(skjema.controls.type.value) || maks === null) return null;
        const antallAlternativer = SpørreskjemaSpørsmålRedigerKomponent.alternativerFor(skjema).length;
        const erGyldig = Number.isInteger(maks) && maks >= 1 && maks <= Math.max(antallAlternativer, 1);
        return erGyldig ? null : {ugyldigMaksimaltAntallValg: {antallAlternativer}};
    }

    private static alternativerPåkrevetValidator(kontroll: AbstractControl): ValidationErrors | null {
        const skjema = kontroll as SpørsmålSkjema;
        const manglerAlternativer = SpørreskjemaSpørsmålRedigerKomponent.harAlternativer(skjema.controls.type.value)
          && skjema.controls.alternativkilde.value === null
          && skjema.controls.alternativer.length === 0;
        return manglerAlternativer ? {manglerAlternativer: true} : null;
    }
}
